/**
 * RadiusServers — Network > RADIUS Servers (BYO registry).
 *
 * An org-scoped registry of RADIUS servers, almost always the CLIENT'S OWN (NPS/AD, ISE,
 * ClearPass, FreeRADIUS). Define once, reference from enterprise SSIDs and captive portals.
 * Reuses the Meridian list+dialog pattern. The shared secret is write-only: shown as "set"
 * once stored, never returned; a blank secret on edit keeps the stored one.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useRadiusServers,
  useCreateRadiusServer,
  useUpdateRadiusServer,
  useDeleteRadiusServer,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { RadiusServer } from "@/types/domain";

export default function RadiusServers() {
  const { data, isLoading, error } = useRadiusServers();
  const [editing, setEditing] = useState<RadiusServer | null>(null);
  const [creating, setCreating] = useState(false);
  const servers = data?.radius_servers ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="RADIUS Servers" subtitle="Register a RADIUS server once, then reference it from SSIDs and portals" />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add server
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading servers…</div>}

      {data && servers.length === 0 && (
        <EmptyState
          title="No RADIUS servers yet"
          hint="Register your RADIUS server (Windows NPS/AD, Cisco ISE, Aruba ClearPass, FreeRADIUS). Then pick it on an enterprise SSID or a captive portal instead of retyping host/secret."
        />
      )}

      {servers.length > 0 && (
        <ul className="flex flex-col gap-2">
          {servers.map((s) => (
            <li key={s.id} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{s.name}</span>
                  {!s.has_secret && (
                    <span className="rounded border border-critical/40 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-critical">
                      no secret
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 font-mono text-xs text-ink-muted">
                  <span>auth {s.auth_host}:{s.auth_port}</span>
                  {s.acct_host && <span>acct {s.acct_host}:{s.acct_port}</span>}
                </div>
              </div>
              <RoleGate min="org_admin" mode="hide">
                <Button variant="secondary" onClick={() => setEditing(s)}>
                  Edit
                </Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {creating && <ServerDialog onClose={() => setCreating(false)} />}
      {editing && <ServerDialog server={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ServerDialog({ server, onClose }: { server?: RadiusServer; onClose: () => void }) {
  const create = useCreateRadiusServer();
  const update = useUpdateRadiusServer();
  const del = useDeleteRadiusServer();
  const editing = !!server;

  const [name, setName] = useState(server?.name ?? "");
  const [authHost, setAuthHost] = useState(server?.auth_host ?? "");
  const [authPort, setAuthPort] = useState(String(server?.auth_port ?? 1812));
  const [acctHost, setAcctHost] = useState(server?.acct_host ?? "");
  const [acctPort, setAcctPort] = useState(String(server?.acct_port ?? 1813));
  const [secret, setSecret] = useState("");

  const pending = create.isPending || update.isPending;
  const err = create.isError || update.isError;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !authHost.trim()) return;
    if (!editing && !secret) return; // secret required on create
    const body = {
      name: name.trim(),
      auth_host: authHost.trim(),
      auth_port: Number(authPort) || 1812,
      acct_host: acctHost.trim() || null,
      acct_port: acctHost.trim() ? Number(acctPort) || 1813 : null,
      ...(secret ? { secret } : {}),
    };
    const onDone = { onSuccess: onClose };
    if (editing) update.mutate({ id: server!.id, ...body }, onDone);
    else create.mutate(body, onDone);
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${server!.name}` : "Add RADIUS server"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Your own RADIUS server — NPS/AD, ISE, ClearPass, FreeRADIUS. The shared secret is stored encrypted and never shown again.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Corp-NPS"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
            </label>
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-2 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Auth host</span>
                <input value={authHost} onChange={(e) => setAuthHost(e.target.value)} placeholder="10.0.0.9"
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Port</span>
                <input inputMode="numeric" value={authPort} onChange={(e) => setAuthPort(e.target.value.replace(/[^0-9]/g, ""))}
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-2 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Accounting host (optional)</span>
                <input value={acctHost} onChange={(e) => setAcctHost(e.target.value)} placeholder="same as auth or blank"
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Port</span>
                <input inputMode="numeric" value={acctPort} onChange={(e) => setAcctPort(e.target.value.replace(/[^0-9]/g, ""))}
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Shared secret{editing ? " (leave blank to keep current)" : ""}</span>
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                placeholder="RADIUS shared secret"
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
            </label>

            {err && <span className="text-xs text-critical">Could not save the server.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(server!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={pending}>
                  {pending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
